/** The nine roles the engine recognizes. Mirrors the engine's `users.role`. */
export const ROLES = [
  'buyer',
  'investor',
  'organizer',
  'artist',
  'agency',
  'venue',
  'sponsor',
  'admin',
  'staff',
] as const;

export type Role = (typeof ROLES)[number];

/**
 * Roles a user can self-register as. `admin` and `staff` are provisioned by
 * Voleto and are never self-serve — the engine rejects them at /auth/register.
 */
export const SELF_REGISTERABLE_ROLES = [
  'buyer',
  'investor',
  'organizer',
  'artist',
  'agency',
  'venue',
  'sponsor',
] as const satisfies readonly Role[];

/**
 * An active admin impersonation of a test persona (task 42).
 *
 * Comes from GET /api/auth/me, never from client state. The banner must reflect what the
 * SERVER believes: a local flag could drift and either hide an active impersonation or
 * show "viewing as" over what is really the admin's own session — both worse than no
 * banner at all.
 */
export interface Impersonation {
  /** Names the gate state, e.g. "Investor — approved AND 506(b) network member". */
  personaLabel: string;
  /** The admin behind the persona. */
  adminEmail: string;
}

/** Shape of GET /api/auth/me. */
export interface User {
  id: string;
  email: string;
  role: Role;
  firstName: string | null;
  lastName: string | null;
  verified: boolean;
  /** Null unless an admin is currently acting as a test persona. */
  impersonation?: Impersonation | null;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterInput extends LoginCredentials {
  role?: Role;
  firstName?: string;
  lastName?: string;
  phone?: string;
}

/**
 * `loading` is the pre-flight state before the first /auth/me resolves. Guarding
 * on it is what stops a signed-in user being bounced to /login on a hard reload.
 */
export interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

export interface AuthContextValue extends AuthState {
  isAuthenticated: boolean;
  login: (credentials: LoginCredentials) => Promise<User>;
  register: (input: RegisterInput) => Promise<User>;
  logout: () => Promise<void>;
  /** Re-fetch identity from the engine (source of truth). */
  refresh: () => Promise<User | null>;
  hasRole: (...roles: Role[]) => boolean;
}
